import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * AuthProvider - Manages authentication state with dual-timeout session system
 * 
 * Session Timeouts:
 * - Idle Timeout (15 min): Resets on every API request
 * - Absolute Timeout (1 hour): Only resets with explicit "Extend Session" action
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  
  // Session timing state
  const [sessionInfo, setSessionInfo] = useState(null);
  const [sessionWarning, setSessionWarning] = useState(false);

  // Check if user was passed via navigation state (from OAuth callback)
  useEffect(() => {
    const userFromState = location.state?.user;
    if (userFromState && !user) {
      console.log('Setting user from OAuth callback state:', userFromState.email);
      setUser(userFromState);
      setLoading(false);
      
      // Clear the state to prevent re-setting
      window.history.replaceState({}, document.title);
    }
  }, [location.state, user]);

  /**
   * Check session status (does NOT refresh idle timeout - it's a polling endpoint)
   */
  const checkSession = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/session`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.authenticated) {
          setUser(data.user);
          
          // Store detailed session info
          setSessionInfo({
            idle_remaining: data.idle_remaining,
            absolute_remaining: data.absolute_remaining,
            effective_remaining: data.effective_remaining,
            idle_minutes: data.idle_minutes,
            absolute_minutes: data.absolute_minutes,
            effective_minutes: data.effective_minutes,
            should_warn_idle: data.should_warn_idle,
            should_warn_absolute: data.should_warn_absolute,
            should_warn: data.should_warn,
            timeout_type: data.timeout_type
          });
          
          // Show warning if either timeout is approaching
          setSessionWarning(data.should_warn || false);
        } else {
          // Session explicitly expired or not authenticated - log out
          setUser(null);
          setSessionInfo(null);
          setSessionWarning(false);
        }
      } else if (response.status === 401) {
        // Explicitly unauthorized - session invalid, log out
        setUser(null);
        setSessionInfo(null);
        setSessionWarning(false);
      }
      // For other error codes (500, 503, etc.), don't log out - might be transient
      // The user's session might still be valid, just a backend hiccup
    } catch (error) {
      // Network error or backend unavailable - DON'T log out
      // This could be a transient network issue, don't terminate the session
      console.error('Session check failed (network):', error);
      // Keep the current user state - don't set to null
    } finally {
      // CRITICAL: Always set loading to false so ProtectedRoute can redirect
      setLoading(false);
    }
  }, []);

  // Check session on mount and periodically (every 30 seconds)
  useEffect(() => {
    checkSession();
    
    // Poll every 30 seconds to update session timing
    const interval = setInterval(checkSession, 30000);
    return () => clearInterval(interval);
  }, [checkSession]);

  /**
   * Login with email and password
   */
  const login = async (email, password) => {
    const response = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || 'Login failed');
    }

    setUser(data.user);
    
    // Set initial session info with hardcoded defaults
    // Then immediately check session to get actual values
    setSessionInfo({
      idle_remaining: 900,  // 15 minutes
      absolute_remaining: 3600,  // 1 hour
      effective_remaining: 900,
      idle_minutes: 15,
      absolute_minutes: 60,
      effective_minutes: 15,
      should_warn_idle: false,
      should_warn_absolute: false,
      should_warn: false,
      timeout_type: 'idle'
    });
    setSessionWarning(false);
    
    // Immediately check session to get actual warning state from backend
    setTimeout(() => checkSession(), 500);
    
    return data;
  };

  /**
   * Logout and clear session
   */
  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setSessionInfo(null);
      setSessionWarning(false);
    }
  };

  /**
   * Refresh session (only resets idle timeout)
   * Called when user interacts with the app
   */
  const refreshSession = async () => {
    try {
      const response = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Update session info
        if (sessionInfo) {
          setSessionInfo({
            ...sessionInfo,
            idle_remaining: data.idle_minutes * 60,
            idle_minutes: data.idle_minutes,
            effective_remaining: Math.min(data.idle_minutes * 60, sessionInfo.absolute_remaining),
            effective_minutes: Math.min(data.idle_minutes, sessionInfo.absolute_minutes),
            should_warn_idle: false,
            should_warn: sessionInfo.should_warn_absolute
          });
        }
        
        // Clear idle warning
        if (sessionInfo?.timeout_type === 'idle') {
          setSessionWarning(false);
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session refresh failed:', error);
      return false;
    }
  };

  /**
   * Extend session (resets BOTH idle AND absolute timeout)
   * Called when user explicitly clicks "Extend Session" button
   */
  const extendSession = async () => {
    try {
      const response = await fetch(`${API}/auth/refresh?force=true`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Update session info with full reset
        setSessionInfo({
          idle_remaining: data.idle_minutes * 60,
          absolute_remaining: data.absolute_minutes * 60,
          effective_remaining: data.effective_minutes * 60,
          idle_minutes: data.idle_minutes,
          absolute_minutes: data.absolute_minutes,
          effective_minutes: data.effective_minutes,
          should_warn_idle: false,
          should_warn_absolute: false,
          should_warn: false,
          timeout_type: 'idle'
        });
        
        // Clear all warnings
        setSessionWarning(false);
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session extend failed:', error);
      return false;
    }
  };

  const value = {
    // User state
    user,
    loading,
    isAuthenticated: !!user,
    
    // Auth actions
    login,
    logout,
    
    // Session management
    checkSession,
    refreshSession,
    extendSession,
    
    // Session timing info
    sessionInfo,
    sessionWarning,
    
    // Convenience getters for backwards compatibility
    minutesRemaining: sessionInfo?.effective_minutes ?? 60
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
